import {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {observer} from 'mobx-react-lite';
import {
  TodoAction,
  TodoCard,
  TodoDateCell,
  TodoEmptyState,
  TodoList,
  CheckinStatusBadge,
  TodoMeta,
  TodoRow,
  TodoRowBody,
  TodoSection,
  TodoSectionHeader,
  TodoSentDate,
  TodoSentDateCell,
  TodoTitle,
} from './my_checkins-style';
import {Body1} from '@ui/atoms/typography';
import {Button} from '@ui/atoms/button';
import {ArrowRightIcon} from '@ui/atoms/icons';
import {useStoreContext} from '@store/store-context';
import {checkinName} from '@utils/checkin-name';
import {capitalize} from '@utils';
import {getWeekOfYear} from '@utils/date';
import dayjs from 'dayjs';
import {ICurrentCheckinsInfoResponseData} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {useQuery} from 'react-query';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {CompanyCheckinsController} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.controller';
import {ItemLoader} from '@ui/organisms/item-loader';
import {EmptyPlaceholder} from '@ui/atoms/empty-placeholder';
import {cloudinary} from '@assets/images/cloudinary';
import {
  formatSentLabel,
  getActionablePendingCheckins,
  getCreatorName,
  getDueMeta,
} from './checkin-submit.utils';
import {
  resolveParticipantCycleMetadata,
  useParticipantCheckinMetadata,
} from './hooks/useParticipantCheckinMetadata';
import {useParticipantCheckinDeadlines} from './hooks/useParticipantCheckinDeadlines';
import {mergeCheckinDeadline} from '../shared/checkin-deadline.utils';

type TodoStatusVariant = 'warning' | 'danger' | 'info' | 'neutral';

interface TodoCheckinItem {
  id: string;
  title: string;
  senderName: string;
  sentLabel: string;
  dueLabel?: string;
  dueDateLabel?: string;
  badgeVariant?: TodoStatusVariant;
}

export const parseName = (data: any) => {
  return data?.firstName && data?.lastName
    ? `${data?.firstName} ${data?.lastName}`
    : data?.email;
};

export const MyCheckins = observer(() => {
  const navigate = useNavigate();
  const {
    checkinsStore: {
      setActiveWorkspaceCheckinId,
      setWorkspaceCheckins,
      workspaceCheckinsList,
    },
    usersStore: {users},
    authStore: {auth},
  } = useStoreContext();

  const checkinLabel = useMemo(() => capitalize(checkinName()), []);

  const emptyState = useMemo(
    () => ({
      image: {
        alt: 'All caught up',
        shadow: '-10px 44px 28px 0 rgba(19, 17, 17, 0.12)',
        src: cloudinary.emptyGoals,
        style: {height: '140px', width: '130px'},
      },
      title: `You're all caught up`,
      subtitle: `No ${checkinLabel}s need your attention right now.`,
    }),
    [checkinLabel],
  );

  const checkinsRequest = useMemo(() => new CompanyCheckinsRequestImpl(), []);

  const checkinsController = useMemo(
    () => new CompanyCheckinsController(checkinsRequest),
    [checkinsRequest],
  );

  const filters = useMemo(
    () => ({
      weekOfYear: getWeekOfYear(),
      year: new Date().getFullYear(),
      include: 'cycle' as const,
    }),
    [],
  );

  const {isLoading: isLoadingCheckins} = useQuery(
    ['my-checkins', filters.weekOfYear, filters.year, filters.include],
    () => checkinsController.getCurrentCheckinsInfo(filters),
    {
      onSuccess: (response) => {
        setWorkspaceCheckins(
          (response ?? []) as ICurrentCheckinsInfoResponseData[],
        );
      },
      onError: () => {
        setWorkspaceCheckins([] as ICurrentCheckinsInfoResponseData[]);
      },
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
    },
  );

  const {
    metadata: participantMetadata,
    isLoading: isLoadingMetadata,
  } = useParticipantCheckinMetadata(workspaceCheckinsList);
  const deadlineRows = useParticipantCheckinDeadlines(
    auth.user.id,
    auth.user.workspace?.id,
    workspaceCheckinsList.some(
      (checkin) =>
        Boolean(checkin.id) && !checkin.isSubmitted && !checkin.isExcused,
    ),
  );

  const getCycleTitle = useCallback(
    (checkin: any) => {
      if (checkin?.cycleName) return checkin.cycleName;
      if (typeof checkin?.cycle === 'object' && checkin.cycle?.cycleName) {
        return checkin.cycle.cycleName;
      }
      return resolveParticipantCycleMetadata(checkin, participantMetadata)
        ?.cycleName;
    },
    [participantMetadata],
  );

  const getCheckinTitle = useCallback(
    (checkin: any) => {
      if (!checkin || Object.keys(checkin).length === 0) {
        return checkinLabel;
      }
      const cycleTitle = getCycleTitle(checkin);
      return cycleTitle || checkinLabel;
    },
    [checkinLabel, getCycleTitle],
  );

  const buildLists = useMemo(() => {
    const pending: TodoCheckinItem[] = [];
    const completed: TodoCheckinItem[] = [];
    const actionablePendingIds = new Set(
      getActionablePendingCheckins(workspaceCheckinsList).map(
        (checkin) => checkin.id,
      ),
    );

    workspaceCheckinsList.forEach((checkin) => {
      if (!checkin?.id) {
        return;
      }

      if (checkin?.isSubmitted) {
        const cycle = resolveParticipantCycleMetadata(
          checkin,
          participantMetadata,
        );
        const filledLabel = checkin?.submittedAt
          ? `Filled ${dayjs(checkin.submittedAt).format('MMM D')}`
          : 'Filled';
        completed.push({
          id: checkin.id as string,
          title: getCheckinTitle(checkin),
          senderName: getCreatorName(cycle?.createdBy, users),
          sentLabel: formatSentLabel(checkin),
          dueLabel: filledLabel,
          badgeVariant: 'info',
        });
      } else {
        if (!actionablePendingIds.has(checkin.id)) return;
        const cycle = resolveParticipantCycleMetadata(
          checkin,
          participantMetadata,
        );
        const dueMeta = getDueMeta(
          mergeCheckinDeadline(checkin, deadlineRows.get(checkin.id)),
        );
        pending.push({
          id: checkin.id as string,
          title: getCheckinTitle(checkin),
          senderName: getCreatorName(cycle?.createdBy, users),
          sentLabel: formatSentLabel(checkin),
          dueLabel: dueMeta.dueLabel,
          dueDateLabel: dueMeta.dueDateLabel,
          badgeVariant: dueMeta.isOverdue ? 'danger' : 'warning',
        });
      }
    });

    return {pending, completed};
  }, [
    getCheckinTitle,
    participantMetadata,
    deadlineRows,
    users,
    workspaceCheckinsList,
  ]);

  const pendingCheckinsList = buildLists.pending;

  const completedCheckinsList = buildLists.completed;

  const hasCheckins =
    pendingCheckinsList.length > 0 || completedCheckinsList.length > 0;

  const renderSection = useCallback(
    ({
      title,
      items,
      emptyMessage,
      buttonLabel,

      onClick,
    }: {
      title: string;
      items: TodoCheckinItem[];
      emptyMessage: string;
      buttonLabel: string;
      buttonKind: 'primary' | 'secondary';
      onClick: (checkinId?: string) => void;
    }) => (
      <TodoSection>
        <TodoSectionHeader>
          <Body1 weight="bold">
            {title} ({items.length})
          </Body1>
        </TodoSectionHeader>
        {items.length ? (
          <TodoList>
            {items.map((item) => (
              <TodoRow key={item.id}>
                <TodoRowBody>
                  <TodoTitle title={item.title}>{item.title}</TodoTitle>
                  <TodoMeta title={`Sent by ${item.senderName}`}>
                    Sent by {item.senderName}
                  </TodoMeta>
                </TodoRowBody>
                <TodoSentDateCell>
                  <TodoSentDate>{item.sentLabel}</TodoSentDate>
                </TodoSentDateCell>
                <TodoDateCell>
                  <CheckinStatusBadge
                    data-testid="checkin-status-badge"
                    variant={item.badgeVariant}>
                    {item.dueLabel && (
                      <span className="due-state">{item.dueLabel}</span>
                    )}
                    {item.dueDateLabel && <span>{item.dueDateLabel}</span>}
                  </CheckinStatusBadge>
                </TodoDateCell>
                <TodoAction>
                  <Button
                    kind={'secondary'}
                    style={{padding: '8px 16px'}}
                    onClick={() => onClick(item.id)}
                    disabled={!item.id}>
                    <span className="flex items-center" style={{gap: 6}}>
                      {buttonLabel}
                      {buttonLabel === 'Start' && <ArrowRightIcon />}
                    </span>
                  </Button>
                </TodoAction>
              </TodoRow>
            ))}
          </TodoList>
        ) : (
          <TodoEmptyState>{emptyMessage}</TodoEmptyState>
        )}
      </TodoSection>
    ),
    [],
  );

  const handleStartCheckin = useCallback(
    (checkinId?: string) => {
      if (!checkinId) return;
      setActiveWorkspaceCheckinId(checkinId);
      navigate(
        `/checkins/my-checkin?checkinId=${encodeURIComponent(
          checkinId,
        )}&mode=edit`,
      );
    },
    [navigate, setActiveWorkspaceCheckinId],
  );

  const handleViewCheckin = useCallback(
    (checkinId?: string) => {
      if (!checkinId) return;
      setActiveWorkspaceCheckinId(checkinId);
      navigate(
        `/checkins/my-checkin?checkinId=${encodeURIComponent(
          checkinId,
        )}&mode=view`,
      );
    },
    [navigate, setActiveWorkspaceCheckinId],
  );

  const showInitialLoader = isLoadingCheckins || isLoadingMetadata;

  return (
    <TodoCard className="bg-white">
      {showInitialLoader ? (
        <div className="flex justify-center py-10">
          <ItemLoader />
        </div>
      ) : hasCheckins ? (
        <>
          {pendingCheckinsList.length > 0 &&
            renderSection({
              title: 'Pending',
              items: pendingCheckinsList,
              emptyMessage: `No pending ${checkinName()}s`,
              buttonLabel: 'Start',
              buttonKind: 'primary',
              onClick: handleStartCheckin,
            })}
          {completedCheckinsList.length > 0 &&
            renderSection({
              title: 'Completed',
              items: completedCheckinsList,
              emptyMessage: `No completed ${checkinName()}s yet`,
              buttonLabel: 'View',
              buttonKind: 'secondary',
              onClick: handleViewCheckin,
            })}
        </>
      ) : (
        <div className="flex justify-center py-10 px-4">
          <EmptyPlaceholder {...emptyState} />
        </div>
      )}
    </TodoCard>
  );
});

export default MyCheckins;
